// Manages the guest's "saved venues" / wishlist. The set of liked venue IDs
// is fetched once on mount and then mutated optimistically. The hook works
// for the logged-out case too (no-op) so it can be safely dropped into any
// guest-facing component.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STORAGE_KEY = 'allureVenueFavorites';

const readCache = () => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeCache = (ids) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  } catch {
    // ignore
  }
};

const FAVORITES_EVENT = 'allure:favorites-changed';

const dispatchChange = (ids) => {
  try {
    window.dispatchEvent(
      new CustomEvent(FAVORITES_EVENT, { detail: { ids } }),
    );
  } catch {
    // ignore
  }
};

export function useFavoriteVenues() {
  const [favoriteIds, setFavoriteIds] = useState(() => new Set(readCache()));
  const inFlightRef = useRef(new Map()); // venueId -> latest desired state

  const token = useMemo(() => {
    try {
      return window.localStorage.getItem('token') || '';
    } catch {
      return '';
    }
  }, []);
  const headers = useMemo(
    () => (token ? { Authorization: `Bearer ${token}` } : {}),
    [token],
  );

  // Hydrate from the backend on mount (overrides the cache so other devices
  // stay in sync).
  useEffect(() => {
    let cancelled = false;
    if (!token) return undefined;
    axios
      .get(`${API}/guest/favorites/ids`, { headers })
      .then((res) => {
        if (cancelled) return;
        const ids = Array.isArray(res.data) ? res.data : [];
        const set = new Set(ids);
        setFavoriteIds(set);
        writeCache(Array.from(set));
        dispatchChange(Array.from(set));
      })
      .catch(() => {
        // Network/auth failure — keep using the cache; toggle will retry.
      });
    return () => {
      cancelled = true;
    };
  }, [headers, token]);

  // Keep other instances of the hook in sync.
  useEffect(() => {
    const handler = (event) => {
      const ids = event?.detail?.ids;
      if (!Array.isArray(ids)) return;
      setFavoriteIds(new Set(ids));
    };
    window.addEventListener(FAVORITES_EVENT, handler);
    return () => window.removeEventListener(FAVORITES_EVENT, handler);
  }, []);

  const isFavorite = useCallback(
    (venueId) => favoriteIds.has(venueId),
    [favoriteIds],
  );

  const persist = useCallback(
    async (venueId, nextFavorited) => {
      if (!token) return; // logged-out — local-only toggle is fine
      inFlightRef.current.set(venueId, nextFavorited);
      try {
        if (nextFavorited) {
          await axios.post(
            `${API}/guest/favorites/${venueId}`,
            null,
            { headers },
          );
        } else {
          await axios.delete(`${API}/guest/favorites/${venueId}`, {
            headers,
          });
        }
        // If a newer toggle landed during the request, fire that too.
        const desired = inFlightRef.current.get(venueId);
        if (desired !== undefined && desired !== nextFavorited) {
          await persist(venueId, desired);
        } else {
          inFlightRef.current.delete(venueId);
        }
      } catch {
        // Roll back to whatever the server thinks; cheap fetch.
        try {
          const res = await axios.get(`${API}/guest/favorites/ids`, {
            headers,
          });
          const ids = Array.isArray(res.data) ? res.data : [];
          const set = new Set(ids);
          setFavoriteIds(set);
          writeCache(Array.from(set));
          dispatchChange(Array.from(set));
        } catch {
          // network down — leave optimistic value in place
        }
        inFlightRef.current.delete(venueId);
      }
    },
    [headers, token],
  );

  const setFavorite = useCallback(
    (venueId, nextFavorited) => {
      if (!venueId) return;
      setFavoriteIds((current) => {
        const next = new Set(current);
        if (nextFavorited) next.add(venueId);
        else next.delete(venueId);
        const ids = Array.from(next);
        writeCache(ids);
        dispatchChange(ids);
        return next;
      });
      persist(venueId, nextFavorited);
    },
    [persist],
  );

  const toggleFavorite = useCallback(
    (venueId) => {
      if (!venueId) return;
      setFavorite(venueId, !favoriteIds.has(venueId));
    },
    [favoriteIds, setFavorite],
  );

  return {
    favoriteIds,
    favoriteCount: favoriteIds.size,
    isFavorite,
    toggleFavorite,
    setFavorite,
  };
}
